import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import { Link, useParams } from "react-router-dom";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import {
  updateClinicalService,
  updateMedicationReview,
} from "../../../../../redux/modules/clinical-services/actions";
import { getCurrentUserDetails } from "../../../../../redux/modules/user/actions";
import { ClinicalServiceTypes, getValidStep } from "../../../text/data";
import { consultedSources, checklist } from "./data";
import CustomInput from "../../../../common/CustomInput";
import SubNavigation from "../../common/sub-navigation";
import { preferencesOptions } from "../../../create/options";
import { AMRBillingOptions } from "../education/data";
import {
  formatDateToYYYYMMDD,
  getTimeDifference,
} from "../../../../../helpers/functions";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";

const DocumentationCheckList = () => {
  const { clinicalServiceObj, clinicalServiceName, clinicalServiceSteps } =
    useSelector((state) => state.clinicalService);
  const { patient } = useSelector((state) => state.patients);
  const { currentLoggedInUserDetail } = useSelector((state) => state.currentLoggedInUserDetail);
  const usersState = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const { serviceId } = useParams();

  // Helper to compute pharmacist full name once
  const getFullName = (u) => (u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : null);
  const pharmacistName =
    getFullName(currentLoggedInUserDetail) ??
    getFullName(usersState?.currentLoggedInUserDetail) ??
    "-";

  const {
    currentStep,
    currentSubStep,
    startDateTime,
    endDateTime,
    documentation,
  } = clinicalServiceObj?.initialAssessment?.initialAssessmentForm || {};
  const [sourceChecks, setSourceChecks] = useState([]);
  const [sourceCheckData, setSourceCheckData] = useState([]);
  const [validateSourceChecks, setValidateSourceChecks] = useState(false);
  const [sourceComments, setSourceComments] = useState("");
  const [checkListChecks, setCheckListChecks] = useState([]);
  const [checkListCheckData, setCheckListCheckData] = useState([]);
  const [validateCheckListChecks, setValidateCheckListChecks] = useState(false);
  const [checkListComments, setCheckListComments] = useState("");
  const { showReferral } =
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm || {};
  const [showRef, setShowRef] = useState(showReferral ?? false);
  const [billingInformation, setBillingInformation] = useState("amrReviewPIN");
  const [openServiceCompleteDialog, setOpenServiceCompleteDialog] =
    useState(false);
  const [serviceEndTime, setServiceEndTime] = useState(
    new Date().toISOString()
  );
  const [noInterventionConfirmation, setNoInterventionConfirmation] =
    useState(null);

  useEffect(() => {
    setServiceTypeView();
    setServiceTypeData();
  }, [clinicalServiceObj, clinicalServiceName]);

  // Fetch user details if not available
  useEffect(() => {
    if (!currentLoggedInUserDetail) {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      const userId = userInfo.userId;
      
      if (userId) {
        dispatch(getCurrentUserDetails(userId, false));
      }
    }
  }, [currentLoggedInUserDetail, dispatch]);

  useEffect(() => {
    if (
      clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.documentation
    ) {
    }
  }, [clinicalServiceObj?.initialAssessment?.initialAssessmentForm]);

  const setServiceTypeView = () => {
    switch (clinicalServiceName) {
      case ClinicalServiceTypes.MedicationReview:
        {
          setSourceCheckData(consultedSources);
          setCheckListCheckData(checklist);
        }
        break;
      default:
        break;
    }
  };

  const setServiceTypeData = () => {
    const doc =
      clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.documentation;
    const preAssessment =
      clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.preAssessment;
    
    if (!preAssessment) return;
    switch (clinicalServiceName) {
      case ClinicalServiceTypes.MedicationReview:
        {
          let sources = [];
          let checkList = checklist
            .map((x) => x.label)
            .filter(
              (x) =>
                x !== "Medications removed from home by the Pharmacist (if applicable)" &&
                x !== "Other (Specify)"
            );
          // selectAllSources(false);
          sources.push("Pharmacy Profile");
          sources.push("Patient");
          if (preAssessment?.attendeeType !== "Patient")
            sources.push("Caregiver/ Agent/ Family");
          if (
            preAssessment?.reasonsForMedicationReview?.some((x) =>
              x.includes("Initiate compliance packaging")
            ) ||
            patient?.preferences?.split(",")?.includes("blisterPack")
          )
            sources.push("Medication Packages/ Compliance or Blister Package");
          if (
            preAssessment?.reasonsForMedicationReview?.some((x) =>
              x.includes("I had a recent discharge from hospital.")
            ) ||
            preAssessment?.reasonsForMedicationReview?.some((x) =>
              x.includes("I have a planned upcoming hospital admission.")
            ) ||
            patient?.contactInfo?.longTermCare === true
          )
            sources.push("Hospital/ Other Facility");

          if (doc?.completenessChecklist || doc?.consultedSources) {
            doc?.consultedSources?.map((x) => {
              if (!sources.includes(x)) sources.push(x);
            });
            doc?.completenessChecklist?.map((x) => {
              if (!checkList.includes(x)) checkList.push(x);
            });
            setSourceComments(doc?.consultedSourcesComments || "");
            setCheckListComments(doc?.completenessChecklistComments || "");
          }
          setSourceChecks(sources);
          setCheckListChecks(checkList);
          setBillingInformation(
            doc?.billingInformation ?? "amrReviewPIN"
          );
        }
        break;
      default:
        break;
    }
  };

  const handleSourceChange = (e) => {
    if (e.target.checked) {
      setSourceChecks([...sourceChecks, e.target.name]);
    } else {
      setSourceChecks(sourceChecks.filter((item) => item !== e.target.name));
    }
  };

  const handleChecklistChange = (e) => {
    if (e.target.checked) {
      setCheckListChecks([...checkListChecks, e.target.name]);
    } else {
      setCheckListChecks(
        checkListChecks.filter((item) => item !== e.target.name)
      );
    }
  };

  const selectAllSources = (isSource) => {
    if (isSource) setSourceChecks(sourceCheckData.map((item) => item.label));
    else setCheckListChecks(checkListCheckData.map((item) => item.label));
  };

  useEffect(() => {
    if (
      sourceChecks.length === sourceCheckData.length &&
      sourceChecks.length > 0
    ) {
      setValidateSourceChecks(true);
    } else {
      setValidateSourceChecks(false);
    }
  }, [sourceChecks]);

  useEffect(() => {
    if (
      checkListChecks.length === checkListCheckData.length &&
      checkListChecks.length > 0
    ) {
      setValidateCheckListChecks(true);
    } else {
      setValidateCheckListChecks(false);
    }
  }, [checkListChecks]);

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatch(updateMedicationReview(serviceId, updatedSteps));
  };

  const onNext = (submitted = false) => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );
      const updatedSteps = {
        documentation: {
          ...documentation,
          consultedSources: sourceChecks,
          completenessChecklist: checkListChecks,
          consultedSourcesComments: sourceComments,
          completenessChecklistComments: checkListComments,
          noInterventionConfirmation: noInterventionConfirmation ? "yes" : "no",
          // mark that the service was submitted (final Complete clicked)
          isServiceSubmitted: submitted ? true : documentation?.isServiceSubmitted || false,
          billingInformation: billingInformation,
          pharmacyLocation: "pharmacyLocation",
        },
        startDateTime: startDateTime ?? new Date().toISOString(),
        endDateTime: serviceEndTime,
        currentStep: nextStep,
        currentSubStep: nextSubStep,
        serviceSteps: clinicalServiceSteps,
      };

      dispatch(updateMedicationReview(serviceId, updatedSteps));
    }
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Documentation - Sources / Checklist
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div className="flex flex-col ">
                <div className="flex flex-row gap-6">
                  <div className="h-[589px] w-[854px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background.png)`,
                      }}
                    >
                      <h3 className="text-lg font-bold text-teal-300 ">
                        Sources Consulted For Service
                      </h3>
                    </div>
                    <div className="flex flex-col gap-6 p-5  text-gray-800 w-[854px] overflow-y-auto overflow-x-hidden bg-scroll max-h-[530px]">
                      <p className="font-semibold text-body-md text-gray-800 ">
                        Select Which Applies.{" "}
                      </p>
                      <div className="relative flex gap-3 flex-col h-auto">
                        {sourceCheckData.map((item, index) => (
                          <div
                            key={item.label || index}
                            className={`$${''} ${
                              item.label === "Caregiver/ Agent/ Family"
                                ? "mb-2 border-b border-gray-100 pb-4"
                                : ""
                            }`}
                          >
                            <span className="flex items-start text-body-md font-normal">
                              <input
                                type="checkbox"
                                id={index}
                                name={item.label}
                                checked={sourceChecks.includes(item.label)}
                                onChange={handleSourceChange}
                              />
                              <label
                                htmlFor={index}
                                className="ml-4 text-gray-800  font-normal "
                              >
                                {item.label}
                              </label>
                            </span>
                          </div>
                        ))}
                      </div>
                      <CustomInput
                        name="comments"
                        className="col-span-12"
                        label=""
                        placeholder="Comments"
                        value={sourceComments}
                        onChange={(e) => setSourceComments(e.target.value)}
                        type="text"
                      />
                    </div>
                  </div>
                  <div className="h-[589px] w-[854px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background.png)`,
                      }}
                    >
                      <h3 className="text-lg font-bold text-teal-300 ">
                        Checklist for Completeness
                      </h3>
                    </div>
                    <div className="flex flex-col gap-6 p-5  text-gray-800 w-[854px] overflow-y-auto overflow-x-hidden bg-scroll max-h-[530px]">
                      <p className="font-semibold text-body-md text-gray-800 ">
                        Select Which Applies.{" "}
                        <span
                          onClick={() => selectAllSources(false)}
                          className="cursor-pointer  ml-4 text-black underline underline-offset-2 font-medium"
                        >
                          Select All
                        </span>
                      </p>
                      <div className="relative flex gap-3 flex-col h-auto">
                        {checkListCheckData.map((item, index) => (
                          <span
                            key={index}
                            className="flex items-start text-body-md font-normal"
                          >
                            <input
                              type="checkbox"
                              id={index}
                              name={item.label}
                              checked={checkListChecks.includes(item.label)}
                              onChange={handleChecklistChange}
                            />
                            <label
                              htmlFor={index}
                              className="ml-4 text-gray-800  font-normal "
                            >
                              {item.label}
                            </label>
                          </span>
                        ))}
                      </div>
                      <CustomInput
                        name="comments"
                        className="col-span-12"
                        label=""
                        placeholder="Comments"
                        value={checkListComments}
                        onChange={(e) => setCheckListComments(e.target.value)}
                        type="text"
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="grid grid-flow-col gap-6 scroll-smooth auto-cols-[415px]  overflow-auto "
                  id="follow-up"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            // disabled={!validateSourceChecks ||}
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] `}
            onClick={() => {
              setServiceEndTime(new Date().toISOString());
              setNoInterventionConfirmation(true);
              setOpenServiceCompleteDialog(true);
            }}
          />
        </div>
      </div>

      {/* assessment complete dialog */}
      <ConfirmationModalNew
        open={openServiceCompleteDialog}
        title="Confirm Annual Medication Review Complete"
        className="!w-[600px] !max-w-[600px] [&_h3]:text-teal-300"
        confirmText="Complete"
        disableConfirm={billingInformation === ""}
        content={() => (
          <div>
            <div className="flex justify-between">
              <div>
                <span>
                  <b>Date:</b> {formatDateToYYYYMMDD(new Date())}
                </span>
              </div>
              <div>
                <span>
                  <b>Time Spent:</b>{" "}
                  { getTimeDifference(startDateTime, new Date().toISOString())}
                </span>
              </div>
            </div>
            <div className="flex justify-start mt-2">
              <div>
                <span>
                  <b>Pharmacist:</b> {pharmacistName}
                </span>
              </div>
            </div>
            <CustomInput
              options={AMRBillingOptions}
              name="billingInformation"
              className="col-span-12 mt-4"
              label="Billing Information"
              type="dropdown"
              value={billingInformation}
              required
              onChange={(e) => setBillingInformation(e.value)}
            />
            <div className="flex justify-between pt-2">
              <div>
                <span className="text-teal-300 text-sm">
                  It is the Pharmacist's responsibility to ensure the PIN is accurate. {"   "}({" "}
                  <a
                    className="underline text-teal-300 text-sm font-semibold"
                    target="_blank"
                    href="https://files.ontario.ca/moh-medscheck-professional-pharmacy-services-guidebook-2016-07-01.pdf"
                  >
                    Reference
                  </a>{" "}
                  )
                </span>
              </div>
            </div>
          </div>
        )}
        onCross={() => setOpenServiceCompleteDialog(false)}
        handleClose={() => setOpenServiceCompleteDialog(false)}
        confirmAction={() => {
          setOpenServiceCompleteDialog(false);
          onNext(true);
        }}
      />
    </div>
  );
};

export default DocumentationCheckList;
